import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { SvgIconComponent } from '../../../../../shared/components/svg-icon/svg-icon.component';
import { CDFILeadsQuota, QuotaDetail } from '../../models/CDFI-leads-quota';
import { SourceTypes } from '../../../../../shared/models/source-types';
import { FormsModule } from '@angular/forms';
import { CdfiLeadsQuotaService } from '../../services/cdfi-leads-quota.service';
import { DestroyBase } from '../../../../../shared/base-classes/destroy-component-base';
import { takeUntil } from 'rxjs';
import { PopupComponent } from '../../../../../shared/components/popup/popup.component';
import { CreateLeadQuota } from '../../models/create-lead-quota';

@Component({
  selector: 'app-source-quota',
  standalone: true,
  imports: [DecimalPipe, SvgIconComponent, FormsModule, PopupComponent],
  templateUrl: './source-quota.component.html',
  styleUrl: './source-quota.component.scss',
})
export class SourceQuotaComponent extends DestroyBase {
  @Input({ required: true }) sourceTypes!: SourceTypes[];
  @Input({ required: true }) cdfiLeadsQuota!: CDFILeadsQuota;

  @Output() removedAllSourceQuota = new EventEmitter<void>();
  @Output() quotaDetailsUpdated = new EventEmitter<void>();

  deleteError: string | null = null;
  quotaError: string | null = null;
  sourceTypesDropDown: SourceTypes[] = [];
  newQuota = {
    sourceType: null as number | null,
    maxQuota: null as number | null,
  };
  addQuotaError: string | null = null;
  showAddSourceQuotaPopup = false;
  editingQuota: { connectionId: number; sourceType: number } | null = null;
  tempMaxQuota: number | null = null;
  showDeleteConfirmPopup = false;
  selectedSourceQuota: QuotaDetail | null = null;

  cdfiLeadsQuoataService = inject(CdfiLeadsQuotaService);

  showAddSourceQuota(): void {
    this.sourceTypesDropDown = this.sourceTypes.filter(
      (sourceType) =>
        !this.cdfiLeadsQuota?.details.some(
          (detail) => detail.source_type === sourceType.source_type,
        ),
    );
    this.newQuota = {
      sourceType: null,
      maxQuota: null,
    };
    this.addQuotaError = null;
    this.showAddSourceQuotaPopup = true;
  }

  hasAvailableSourceTypes(): boolean {
    return this.sourceTypes.some(
      (sourceType) =>
        !this.cdfiLeadsQuota?.details.some(
          (detail) => detail.source_type === sourceType.source_type,
        ),
    );
  }

  startEdit(
    connectionId: number,
    sourceType: number,
    currentQuota: number,
  ): void {
    this.editingQuota = { connectionId, sourceType };
    this.tempMaxQuota = currentQuota;
    this.quotaError = null;
  }

  cancelEdit(): void {
    this.editingQuota = null;
    this.tempMaxQuota = null;
    this.quotaError = null;
  }

  UpdateSourceQuota(sourceType: number): void {
    if (this.tempMaxQuota === null) return;

    // Get the connection and detail to check the used quota
    const details = this.cdfiLeadsQuota?.details.find(
      (detail) => detail.source_type === sourceType,
    );

    if (!details) {
      this.quotaError = 'Could not find quota details';
      return;
    }

    if (this.tempMaxQuota < details.used_quota) {
      this.quotaError = `Max quota cannot be less than used quota (${details.used_quota})`;
      return;
    }

    this.cdfiLeadsQuoataService
      .updateMaxLeadsQuota(
        this.cdfiLeadsQuota.connection_id,
        this.tempMaxQuota,
        sourceType,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          details.max_quota = this.tempMaxQuota!;
          details.available_quota = details.max_quota - details.used_quota;
          this.quotaDetailsUpdated.emit();
          this.cancelEdit();
        },
        error: () => {
          this.quotaError = 'Failed to update quota';
          setTimeout(() => (this.quotaError = null), 5000); // Clear error after 5 seconds
        },
      });
  }

  deleteConfirmPopupHandler(quotaDetail: QuotaDetail): void {
    this.selectedSourceQuota = quotaDetail;
    this.showDeleteConfirmPopup = true;
  }

  deleteSourceTypeQuota(sourceType: number): void {
    this.cdfiLeadsQuoataService
      .deleteLeadsQuota([sourceType], this.cdfiLeadsQuota.connection_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.cdfiLeadsQuota.details = this.cdfiLeadsQuota.details.filter(
            (detail) => detail.source_type !== sourceType,
          );
          // If no more details, remove the connection
          if (this.cdfiLeadsQuota.details.length === 0) {
            this.removedAllSourceQuota.emit();
          }

          this.deleteError = null;
          this.showDeleteConfirmPopup = false;
        },
        error: () => {
          this.deleteError = 'Failed to delete quota';
          setTimeout(() => (this.deleteError = null), 5000);
          this.showDeleteConfirmPopup = false;
        },
      });
  }

  createSourceQuota(): void {
    if (!this.newQuota.sourceType || !this.newQuota.maxQuota) {
      this.addQuotaError = 'Please fill in all fields';
      return;
    }

    // Check if source type already exists
    if (
      this.cdfiLeadsQuota.details.some(
        (detail) => detail.source_type === this.newQuota.sourceType,
      )
    ) {
      this.addQuotaError =
        'This source type already exists for this connection';
      return;
    }

    const createLeadQuotaModel: CreateLeadQuota[] = [
      {
        source_type: this.newQuota.sourceType,
        max_quota: this.newQuota.maxQuota,
      },
    ];

    this.cdfiLeadsQuoataService
      .createLeadsQuota(this.cdfiLeadsQuota.connection_id, createLeadQuotaModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.quotaDetailsUpdated.emit();
          this.addQuotaError = null;
          this.showAddSourceQuotaPopup = false;
        },
        error: (err) => {
          const errmsg = err?.error[0]?.non_field_errors[0];
          this.addQuotaError = 'Failed to create quota. ' + errmsg;
        },
      });
  }
}
